import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { FailedProvisioningJob } from '@control-center/shared';

import { Input, Select } from '../clients/form-primitives';
import { useFailedJobs } from '../../hooks/use-provisioning';
import { FailedJobCard } from './FailedJobCard';

type ActionFilter = 'all' | 'grant' | 'revoke';

interface ProvisioningDlqPanelProps {
  canManage: boolean;
}

interface ClientGroup {
  clientId: string;
  clientName: string;
  jobs: FailedProvisioningJob[];
}

/** Agrupa os jobs falhados por cliente, preservando a ordem de chegada (AC7). */
function groupByClient(items: FailedProvisioningJob[]): ClientGroup[] {
  const map = new Map<string, ClientGroup>();
  for (const job of items) {
    const existing = map.get(job.client.id);
    if (existing) {
      existing.jobs.push(job);
    } else {
      map.set(job.client.id, {
        clientId: job.client.id,
        clientName: job.client.name,
        jobs: [job],
      });
    }
  }
  return [...map.values()];
}

export function ProvisioningDlqPanel({ canManage }: ProvisioningDlqPanelProps): JSX.Element {
  const [action, setAction] = useState<ActionFilter>('all');
  const [saasSlug, setSaasSlug] = useState('');

  const filters = useMemo(
    () => ({
      ...(action !== 'all' ? { action } : {}),
      ...(saasSlug.trim() ? { saasSlug: saasSlug.trim() } : {}),
      limit: 50,
    }),
    [action, saasSlug],
  );

  const query = useFailedJobs(filters);
  const items = query.data?.pages.flatMap((p) => p.data.items) ?? [];
  const total = query.data?.pages[0]?.data.total ?? items.length;
  const groups = groupByClient(items);

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Select value={action} onChange={(e) => setAction(e.target.value as ActionFilter)}>
          <option value="all">Todas as ações</option>
          <option value="grant">Conceder acesso</option>
          <option value="revoke">Revogar acesso</option>
        </Select>
        <Input
          value={saasSlug}
          onChange={(e) => setSaasSlug(e.target.value)}
          placeholder="Filtrar por SaaS (slug, ex: owltask)"
        />
        <div className="flex items-center justify-end gap-3 text-[12px] text-muted">
          {query.isFetching && <span>Atualizando…</span>}
          {query.data && (
            <span>
              {total} falha{total === 1 ? '' : 's'} · {groups.length} cliente
              {groups.length === 1 ? '' : 's'}
            </span>
          )}
        </div>
      </div>

      {query.isLoading && (
        <div className="rounded-[12px] border border-line bg-panel py-12 text-center text-sm text-muted">
          Carregando falhas…
        </div>
      )}

      {query.isError && (
        <div className="rounded-[12px] border border-bad/40 bg-bad/10 p-4 text-sm text-ink">
          Erro ao carregar as falhas. Tente recarregar a página.
        </div>
      )}

      {query.data && items.length === 0 && (
        <div className="rounded-[12px] border border-good/40 bg-good/10 py-12 text-center text-sm text-ink">
          ✅ Nenhuma falha de provisionamento pendente.
        </div>
      )}

      {groups.length > 0 && (
        <div className="flex flex-col gap-4">
          {groups.map((group) => (
            <section
              key={group.clientId}
              className="rounded-[12px] border border-line bg-panel p-5"
            >
              <div className="mb-3 flex items-center justify-between gap-2">
                <Link
                  to={`/identities/${group.clientId}`}
                  className="text-[14px] font-semibold text-ink underline-offset-2 hover:underline"
                >
                  {group.clientName}
                </Link>
                <span className="rounded-[6px] border border-bad/40 bg-bad/10 px-2 py-[1px] text-[11px] text-bad">
                  {group.jobs.length} falha{group.jobs.length === 1 ? '' : 's'}
                </span>
              </div>
              <div className="flex flex-col gap-2">
                {group.jobs.map((job) => (
                  <FailedJobCard key={job.id} job={job} canManage={canManage} />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}

      {query.hasNextPage && (
        <div className="flex justify-center">
          <button
            type="button"
            onClick={() => void query.fetchNextPage()}
            disabled={query.isFetchingNextPage}
            className="inline-flex h-8 items-center rounded-[6px] border border-line bg-panel-2 px-4 text-[12px] text-ink hover:bg-panel disabled:opacity-50"
          >
            {query.isFetchingNextPage ? 'Carregando…' : 'Carregar mais'}
          </button>
        </div>
      )}
    </div>
  );
}
